import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { fixEncoding } from "utils/text";
import { useApp } from "../../../context/AppContext";
import { useAuth } from "../../../context/AuthContext";
import { getPedido, getPedidoEntregado } from "../services/pedidosFS";
import { cambiarEstado, ESTADOS } from "../services/estados";
import { getFlag } from "../services/featureFlags";
import QrScanner from "../../../components/QrScanner";
import { getCatalogoByCodUru } from "../services/catalogo";
import { getStockTiras, confirmarPedidoConStock } from "../services/stockTiras";
import { db } from "../../../firebase";
import { doc, updateDoc, serverTimestamp, getDoc, getDocs, collection, query, where } from "firebase/firestore";

import KitVinculo from "./KitVinculo";
import { esParteOperario, parteDeProducto, esKitProducto } from "../services/categoriasOverride";
import PackConfirmModal from "./PackConfirmModal";
import PackErrorModal from "./PackErrorModal";
import Modal from "./Modal";
import VolverListaPedidos from "./VolverListaPedidos";

// ============== Helpers ==============
function toURUCode(value) {
  const s = String(value ?? "").trim();
  const mNum = s.match(/\b\d{7,}\b/);
  if (mNum) return mNum[0];
  return s.split(/\s+/)[0].replace(/[^\w-]/g, "");
}
function toCustomerNo(uru) {
  return String(uru || "").trim() || "—";
}
function reqQty(it) {
  return it.cant ?? it.cantidad ?? it.qty ?? 0;
}
function keyFor(it, i) {
  return `${(it.cod || it.codigo || it.codigoURU || "IDX")}-${i}`;
}

// Nombre legible del producto. Prioridad: nombre del CATÁLOGO (importación, customerNo);
// si el catálogo no lo tiene, se usa la descripción del renglón (Finnegans).
// fixEncoding corrige mojibake de Windows-1252 que Finnegans puede enviar mal codificado.
function getNombreProducto(it, cat) {
  const catNombre = cat?.customerNo || cat?.customer_no || "";
  if (catNombre) return fixEncoding(catNombre);
  const desc = it.descripcion || it.desc || it.nombre || it.cod || it.codigo || it.codigoURU || "Producto";
  return fixEncoding(desc);
}

// Lista de productos en modo solo lectura (sin checks ni interacción)
function ListaProductosSoloLectura({ pedido, catalogoMap }) {
  const productos = pedido?.productos || [];
  return (
    <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
      {productos.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 16px", color: "#94a3b8" }}>
          <p style={{ margin: 0 }}>No hay productos en este pedido.</p>
        </div>
      ) : (
        productos.map((it, i) => {
          const k = keyFor(it, i);
          const qty = reqQty(it);
          const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
          const uru = toURUCode(raw);
          const cat = catalogoMap?.[uru] || {};
          const nombre = getNombreProducto(it, cat);
          const color = cat?.finish || cat?.color || "";

          return (
            <div
              key={k}
              style={{
                display: "flex", alignItems: "center", gap: "14px",
                width: "100%", textAlign: "left",
                background: "#fff",
                border: "1.5px solid #e2e8f0",
                borderRadius: "14px",
                padding: "14px 16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  margin: 0, fontSize: "0.85rem", fontWeight: 600,
                  color: "#1e293b", lineHeight: 1.35, wordBreak: "break-word",
                }}>
                  {nombre}
                </p>
                {color && (
                  <p style={{ margin: "2px 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>{color}</p>
                )}
              </div>
              <div style={{
                flexShrink: 0,
                background: "#f1f5f9", color: "#475569",
                fontWeight: 700, fontSize: "0.85rem",
                padding: "4px 10px", borderRadius: "8px",
              }}>
                ×{qty}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ============== Pantalla ==============
export default function PedidoDetalleOperario() {
  const { id: rawId } = useParams();
  const pedidoId = useMemo(() => decodeURIComponent(rawId || ""), [rawId]);
  const { toast, haptics, confirmarAvance } = useApp();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const puedeVincularKits = ["encargado", "admin"].includes((profile?.rol || profile?.role || "").toLowerCase());

  const [pedido, setPedido]         = useState(null);
  const [lite, setLite]             = useState(true);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [autoStarting, setAutoStarting] = useState(false);
  const autoStartedRef = useRef(false);

  const [prep, setPrep]             = useState({});
  const [catalogoMap, setCatalogoMap] = useState({});
  const [checks, setChecks]         = useState({});

  // overlay (solo modo escáner)
  const [scanForKey, setScanForKey]   = useState(null);
  const [packConfirm, setPackConfirm] = useState(null);
  const [packError, setPackError]     = useState(null);

  // problema
  const [showProblema, setShowProblema] = useState(false);
  const [reportando, setReportando]     = useState(false);
  const [problemaForm, setProblemaForm] = useState({ productoIdxs: [], tipo: "FALTA_STOCK", desc: "" });

  // ===== Carga inicial =====
  useEffect(() => {
    (async () => {
      if (!pedidoId) return;
      setLoading(true);
      try {
        let p = await getPedido(pedidoId);
        if (!p) {
          // Pedidos ya entregados se mueven a 'pedidos_entregados' y se borran de 'pedidos'
          p = await getPedidoEntregado(pedidoId);
        }
        setPedido(p || null);
      } catch (e) {
        console.error("[Operario] error cargando pedido:", e);
        setPedido(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [pedidoId]);

  useEffect(() => { (async () => setLite(await getFlag("MODO_LITE")))(); }, []);

  // Catálogo para enriquecer nombres (best-effort)
  useEffect(() => {
    if (!pedido?.productos || !Array.isArray(pedido.productos)) return;
    const codigosURU = Array.from(new Set(
      pedido.productos
        .map(p => p.cod || p.codigo || p.codigoURU || p.desc || p.descripcion || p.nombre)
        .filter(Boolean).map(toURUCode).filter(Boolean)
    ));
    if (codigosURU.length === 0) { setCatalogoMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(
          codigosURU.map(async (uru) => [uru, (await getCatalogoByCodUru(uru)) || null])
        );
        if (!cancelled) setCatalogoMap(Object.fromEntries(entries));
      } catch (e) { console.error("[Operario] catálogo error", e?.code || e); }
    })();
    return () => { cancelled = true; };
  }, [pedido?.productos]);

  // Stock para modo escáner
  useEffect(() => {
    if (lite) return; // no hace falta en modo lite
    let cancel = false;
    (async () => {
      if (!pedido?.productos) return;
      const next = {};
      for (let i = 0; i < pedido.productos.length; i++) {
        const it = pedido.productos[i];
        const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
        const uru = toURUCode(raw);
        if (!uru) continue;
        const stock = await getStockTiras(uru).catch(() => 0);
        next[keyFor(it, i)] = { stock, usarSueltas: 0, paquete: null, usarDePaquete: 0 };
      }
      if (!cancel) setPrep(next);
    })();
    return () => { cancel = true; };
  }, [pedido?.productos, lite]);

  // ¿El usuario actual es responsable de este pedido (por uid o por override de parte)?
  const soyResponsable = useMemo(() => {
    if (!pedido) return false;
    if (pedido.operarioId && pedido.operarioId === user?.uid) return true;
    if ((pedido.responsablesUids || []).includes(user?.uid)) return true;
    return false;
  }, [pedido, user?.uid]);

  // Auto-transición ASIGNADO → EN_PREPARACION
  useEffect(() => {
    if (!pedido || !user?.uid) return;
    if (pedido.estado !== ESTADOS.ASIGNADO) return;
    // Cualquier responsable (operario primario, segundo responsable o encargado que
    // abre su parte) puede iniciar la preparación del pedido.
    if (!soyResponsable) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    setAutoStarting(true);
    (async () => {
      try {
        await cambiarEstado(pedidoId, ESTADOS.EN_PREPARACION);
        const p = await getPedido(pedidoId);
        setPedido(p);
      } catch (e) {
        console.error("[Operario] auto-transition error:", e);
        toast?.error?.("No se pudo iniciar la preparación. Intentá de nuevo.");
        autoStartedRef.current = false;
      } finally {
        setAutoStarting(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.estado, pedido?.operarioId, user?.uid, soyResponsable]);

  // ¿Es ISABELA? El operario de ISABELA finaliza en CONTROLADO (sin paso de control del encargado)
  const esIsabela = (pedido?.deposito || "").trim().toUpperCase() === "ISABELA";

  // Estado del pedido al momento de abrir esta pantalla (para distinguir "recién
  // terminé la preparación" de "estoy revisando un pedido ya controlado").
  const initialEstadoRef = useRef(undefined);
  useEffect(() => {
    if (pedido && initialEstadoRef.current === undefined) {
      initialEstadoRef.current = pedido.estado;
    }
  }, [pedido]);

  // Redirect al completarse (solo si la transición ocurrió en esta sesión,
  // no si el operario está reabriendo un pedido que ya estaba CONTROLADO/PREPARADO).
  useEffect(() => {
    const estadoFinal = esIsabela ? ESTADOS.CONTROLADO : ESTADOS.PREPARADO;
    if (pedido?.estado === estadoFinal && initialEstadoRef.current !== estadoFinal) {
      initialEstadoRef.current = estadoFinal; // evitar re-disparo
      confirmarAvance?.({
        titulo: esIsabela ? "Pedido controlado" : "Pedido preparado",
        mensaje: `#${pedido?.numero || pedido?.id || ""} listo`,
        onOk: () => navigate("/pedidos"),
      });
    }
  }, [pedido?.estado, esIsabela, navigate, confirmarAvance]);

  // ===== Parte del usuario (A: perfiles/cortinas · B: resto) según asignaciones =====
  // null = pedido viejo sin asignaciones o encargado/admin viendo → muestra todo.
  // La parte se deriva SOLO del uid: cada usuario prepara únicamente su parte.
  // Soporta todas las partes (A/ACC/PVC/VID/K/B) buscando el uid en las asignaciones.
  const miParte = useMemo(() => {
    const asg = pedido?.asignaciones;
    if (!asg) return null;
    return Object.keys(asg).find((p) => asg[p]?.uid && asg[p].uid === user?.uid) || null;
  }, [pedido?.asignaciones, user?.uid]);

  // ===== LITE: productos filtrados por la parte del usuario (A perfiles/cortinas · K kits · B resto) =====
  const productosLite = useMemo(() => {
    const all = pedido?.productos || [];
    if (!miParte) return all;
    return all.filter(it => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      return parteDeProducto(uru, catalogoMap?.[uru], it) === miParte;
    });
  }, [pedido?.productos, miParte, catalogoMap]);
  // ===== Componentes de cada kit (del pedido vinculado) → se listan como ítems tildables =====
  const [kitComps, setKitComps] = useState({}); // { [kitUru]: { pedidoId, numero, comps: [] } }
  useEffect(() => {
    const kits = (pedido?.productos || []).map((it) => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      if (!esKitProducto(uru, catalogoMap?.[uru], it)) return null;
      return { uru, descripcion: it.descripcion || it.desc || it.nombre };
    }).filter(Boolean);
    if (kits.length === 0) { setKitComps({}); return; }
    let cancel = false;
    (async () => {
      const result = {};
      let compCandidatos = null; // cache de pedidos del depósito para el camino 2
      const textoDesc = (p) => [p.descripcion, p.observaciones, p.nota, p.comentario, p.detalle].filter(Boolean).join(" ").toUpperCase();
      for (const kt of kits) {
        let compId = pedido?.kitsVinculados?.[kt.uru]?.pedidoId || null;
        // Camino 1: número (4-6 dígitos) dentro de la descripción del kit.
        if (!compId && kt.descripcion) {
          const nums = String(kt.descripcion).match(/\d{4,6}/g) || [];
          for (const n of nums) {
            for (const valor of [Number(n), n]) {
              try {
                const snap = await getDocs(query(collection(db, "pedidos"), where("numero", "==", valor), where("deposito", "==", pedido.deposito || "R8")));
                const found = snap.docs.find(d => d.id !== pedido.id);
                if (found) { compId = found.id; break; }
              } catch (e) { console.error("[kit comp]", e); }
            }
            if (compId) break;
          }
        }
        // Camino 2: pedido con "COMPONENTES KIT" que menciona el N° de ESTE pedido.
        if (!compId) {
          const miNumero = String(pedido?.numero || "").trim();
          if (miNumero) {
            try {
              if (compCandidatos === null) {
                const snap = await getDocs(query(collection(db, "pedidos"), where("deposito", "==", pedido.deposito || "R8")));
                compCandidatos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
              }
              const re = new RegExp(`\\b${miNumero}\\b`);
              const comp = compCandidatos.find(p => p.id !== pedido.id && textoDesc(p).includes("COMPONENTES KIT") && re.test(textoDesc(p)));
              if (comp) compId = comp.id;
            } catch (e) { console.error("[kit comp 2]", e); }
          }
        }
        if (compId) {
          try {
            const snap = await getDoc(doc(db, "pedidos", compId));
            if (snap.exists()) result[kt.uru] = { pedidoId: compId, numero: snap.data().numero, comps: snap.data().productos || [] };
          } catch (e) { console.error("[kit comp load]", e); }
        }
      }
      if (!cancel) setKitComps(result);
    })();
    return () => { cancel = true; };
  }, [pedido?.productos, pedido?.kitsVinculados, pedido?.deposito, pedido?.id, pedido?.numero, catalogoMap]);

  // Cargar el catálogo (Customer No) de los códigos de los componentes de kit,
  // que vienen de otro pedido y no estaban en el catálogo inicial.
  useEffect(() => {
    const faltantes = [];
    Object.values(kitComps).forEach(kc => (kc.comps || []).forEach(c => {
      const uru = toURUCode(c.cod || c.codigo || c.codigoURU || c.desc || c.descripcion || c.nombre || "");
      if (uru && !(uru in catalogoMap)) faltantes.push(uru);
    }));
    const unicos = [...new Set(faltantes)];
    if (unicos.length === 0) return; // ya están todos → no hace nada (evita loops)
    let cancel = false;
    (async () => {
      try {
        const entries = await Promise.all(unicos.map(async uru => [uru, (await getCatalogoByCodUru(uru)) || null]));
        if (!cancel) setCatalogoMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      } catch (e) { console.error("[kit comp catálogo]", e); }
    })();
    return () => { cancel = true; };
  }, [kitComps, catalogoMap]);

  // Lista final de ítems a cargar: cada kit se REEMPLAZA por sus componentes.
  const itemsCargables = useMemo(() => {
    const out = [];
    productosLite.forEach((it, i) => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      const esK = esKitProducto(uru, catalogoMap?.[uru], it);
      if (esK && kitComps[uru]?.comps?.length) {
        const kitNombre = getNombreProducto(it, catalogoMap?.[uru]);
        kitComps[uru].comps.forEach((c, ci) => {
          out.push({ ...c, _key: `kit:${uru}:${ci}`, _kitUru: uru, _kitNombre: kitNombre, _kitNumero: kitComps[uru].numero });
        });
      } else {
        out.push({ ...it, _key: keyFor(it, i) });
      }
    });
    return out;
  }, [productosLite, kitComps, catalogoMap]);

  const totalProductos = itemsCargables.length;
  const checkedCount = itemsCargables.filter(it => !!checks[it._key]).length;
  const allChecked = totalProductos > 0 && checkedCount === totalProductos;
  const progreso = totalProductos > 0 ? Math.round((checkedCount / totalProductos) * 100) : 0;

  // ===== Modificaciones detectadas por el sync de Finnegans =====
  const modInfo = pedido?.modificacionInfo;
  const addedSet = useMemo(
    () => new Set((modInfo?.addedItems || []).map((x) => x.cod)),
    [modInfo]
  );
  const changedMap = useMemo(() => {
    const m = {};
    (modInfo?.changedItems || []).forEach((x) => { m[x.cod] = x; });
    return m;
  }, [modInfo]);
  function getModBadge(uru) {
    if (addedSet.has(uru)) {
      return { label: "NUEVO", bg: "#dcfce7", color: "#15803d" };
    }
    const chg = changedMap[uru];
    if (chg) {
      const diff = chg.cantAhora - chg.cantAntes;
      return diff > 0
        ? { label: `+${diff} más`, bg: "#fef3c7", color: "#b45309" }
        : { label: `${diff} menos`, bg: "#fee2e2", color: "#dc2626" };
    }
    return null;
  }

  // ===== Escáner: productos filtrados por categoría (perfiles + cortinas + kits) =====
  const productosOperario = useMemo(() => {
    return (pedido?.productos || []).filter((it) => {
      const raw = it.cod || it.descripcion || it.desc || "";
      const uru = toURUCode(raw);
      return esParteOperario(uru, catalogoMap?.[uru]);
    });
  }, [pedido?.productos, catalogoMap]);

  // ===== Helpers escáner =====
  function getItemInfoByKey(k) {
    if (!pedido?.productos) return null;
    const idx = Number(String(k).split("-").pop());
    const it = pedido.productos[idx];
    if (!it) return null;
    const need = reqQty(it);
    const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
    const uruExpected = toURUCode(raw);
    return { it, idx, need, uruExpected };
  }

  function getRemainingForKey(k) {
    const info = getItemInfoByKey(k);
    if (!info) return 0;
    const p = prep[k] || {};
    return Math.max(0, info.need - Number(p.usarSueltas || 0) - Number(p.usarDePaquete || 0));
  }

  function clampPrepForNeed(k, draft) {
    const p = { ...(prep[k] || {}), ...draft };
    const info = getItemInfoByKey(k);
    if (!info) return p;
    const { need } = info;
    const stock = Number(p.stock || 0);
    const packSize = Number(p.paquete?.packSize || 0);
    let usarSueltas = Math.min(Number(p.usarSueltas || 0), stock);
    let usarDePaquete = Math.min(Number(p.usarDePaquete || 0), packSize);
    const total = usarSueltas + usarDePaquete;
    if (total > need) {
      const exceso = total - need;
      if (usarDePaquete >= exceso) usarDePaquete -= exceso;
      else usarSueltas = Math.max(0, usarSueltas - (exceso - usarDePaquete));
    }
    return { ...p, usarSueltas, usarDePaquete };
  }

  function onScanPaquete(payload) {
    try {
      if (!scanForKey) return;
      const info = getItemInfoByKey(scanForKey);
      if (!info) { toast?.error?.("No se encontró el producto seleccionado"); return; }
      const { uruExpected } = info;
      const codUruQR = toURUCode(payload.codUru || payload.codigo_urualum || payload.codigo || "");
      const packSize = Number(payload.packSize || payload.cantidad || 0);
      if (!codUruQR || !packSize) { toast?.error?.("QR inválido: faltan datos"); return; }
      if (codUruQR !== uruExpected) {
        setPackError({ esperado: uruExpected, qr: codUruQR });
        return;
      }
      const remaining = getRemainingForKey(scanForKey);
      const defaultQty = Math.min(remaining, packSize);
      const nombre = catalogoMap?.[uruExpected]?.customerNo || toCustomerNo(uruExpected);
      setPackConfirm({ key: scanForKey, nombre, packSize, remaining, defaultQty });
      setScanForKey(null);
    } catch (e) {
      console.error(e);
      toast?.error?.("No se pudo procesar el QR");
    }
  }

  // ===== Acciones =====
  function toggleCheck(k) { setChecks(prev => ({ ...prev, [k]: !prev[k] })); }

  function marcarTodo() {
    const next = {};
    itemsCargables.forEach((it) => { next[it._key] = true; });
    setChecks(next);
  }

  async function finalizarPreparacion() {
    if (saving) return;
    try {
      setSaving(true);
      const asg = pedido?.asignaciones;
      const partesPresentes = asg ? Object.keys(asg) : [];

      // ── Flujo de dos partes (R8): marca mi parte; el pedido pasa a PREPARADO
      //    solo cuando TODAS las partes están terminadas. ──
      if (!esIsabela && miParte && partesPresentes.length > 0) {
        const nuevasPartes = { ...(pedido.partesPreparadas || {}), [miParte]: true };
        const todasListas = partesPresentes.every(p => nuevasPartes[p] === true);

        // Todas las partes listas → el pedido pasa a PREPARADO (celebración por el
        // flujo normal: pantalla ✅ + modal por el useEffect que observa el estado).
        if (todasListas) {
          await cambiarEstado(pedidoId, ESTADOS.PREPARADO, { partesPreparadas: nuevasPartes });
          haptics?.success?.();
          toast?.success?.("¡Pedido preparado! (todas las partes listas)");
          const p = await getPedido(pedidoId);
          setPedido(p);
          return;
        }

        // Falta al menos otra parte: el estado no cambia, así que mostramos el mismo
        // modal de confirmación (tick verde + sonido) para que el usuario sepa que
        // SU parte quedó registrada, y lo devolvemos a su lista de pedidos.
        await updateDoc(doc(db, "pedidos", pedido.id || pedidoId), { partesPreparadas: nuevasPartes });
        haptics?.success?.();
        const p = await getPedido(pedidoId);
        setPedido(p);
        confirmarAvance?.({
          titulo: "¡Tu parte quedó lista!",
          mensaje: `Preparaste tu parte del pedido #${pedido?.numero || pedido?.id || ""}. Falta que se termine la otra parte.`,
          onOk: () => navigate(puedeVincularKits ? "/pedidos" : "/operario/asignados"),
        });
        return;
      }

      // ── Flujo clásico (ISABELA, o pedidos sin asignaciones por parte) ──
      await updateDoc(doc(db, "pedidos", pedido.id || pedidoId), { prepPerfilesOk: true });
      const estadoDestino = esIsabela ? ESTADOS.CONTROLADO : ESTADOS.PREPARADO;
      await cambiarEstado(pedidoId, estadoDestino);
      haptics?.success?.();
      toast?.success?.("¡Pedido preparado!");
      const p = await getPedido(pedidoId);
      setPedido(p);
    } catch (e) {
      console.error(e);
      toast?.error?.(e.message || "No se pudo confirmar la preparación");
    } finally {
      setSaving(false);
    }
  }

  async function reportarProblema() {
    if (reportando) return;
    const idxs = problemaForm.productoIdxs || [];
    if (idxs.length === 0) { toast?.error?.("Marcá al menos un producto con problema."); return; }
    try {
      setReportando(true);
      const esFaltaStock = problemaForm.tipo === "FALTA_STOCK";
      const desc = problemaForm.desc.trim();
      const productos = pedido?.productos || [];

      // Un renglón de error por cada producto marcado.
      const errorProductos = idxs.map((i) => {
        const it = productos[i];
        const raw = it ? (it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "") : "";
        const uru = toURUCode(raw);
        const nombre = it ? getNombreProducto(it, catalogoMap[uru] || {}) : "Producto desconocido";
        return {
          cod: it?.cod || it?.codigo || null,
          nombre,
          tipo: esFaltaStock ? "sin_stock" : "otro",
          descripcion: desc || null,
        };
      });
      const detalle = errorProductos
        .map((p) => `• ${p.nombre}: ${esFaltaStock ? "Falta de stock" : "Otro motivo"}${desc ? ` — ${desc}` : ""}`)
        .join("\n");

      await cambiarEstado(pedido.id || pedidoId, ESTADOS.CON_ERROR, {
        errorDetalle: detalle,
        errorProductos,
        errorProductoCod: errorProductos[0]?.cod || null,
        errorProductoNombre: errorProductos.map((p) => p.nombre).join(", "),
      });

      setShowProblema(false);
      haptics?.success?.();
      confirmarAvance?.({
        titulo: "Error reportado",
        mensaje: `#${pedido?.numero || pedido?.id || ""} — el encargado lo revisará`,
        onOk: () => navigate("/pedidos"),
      });
    } catch (e) {
      console.error(e);
      toast?.error?.("No se pudo reportar. Intentá de nuevo.");
    } finally {
      setReportando(false);
    }
  }

  // ===== Estados de carga/acceso =====
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <p style={{ color: "#94a3b8", fontSize: "0.9rem" }}>Cargando pedido…</p>
    </div>
  );
  if (!pedido) return (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <p style={{ color: "#ef4444" }}>Pedido no encontrado.</p>
    </div>
  );
  // Acceso: el responsable primario, cualquier segundo responsable, o el encargado/admin.
  if (pedido.operarioId && !soyResponsable && !puedeVincularKits) return (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <p style={{ color: "#ef4444" }}>No tenés acceso a este pedido.</p>
    </div>
  );

  // Pedido dividido por partes (R8) donde el usuario no es responsable de ninguna:
  // no debe preparar nada acá (p. ej. el encargado abriendo la parte de otro operario)
  // → lo derivamos al detalle de gestión, donde puede ver el responsable y reasignar.
  const tieneAsignaciones = pedido.asignaciones && Object.keys(pedido.asignaciones).length > 0;
  if (!esIsabela && tieneAsignaciones && !miParte) {
    return <Navigate to={`/pedidos/${encodeURIComponent(pedido.id || pedidoId)}`} replace />;
  }

  // ===== RENDER =====
  const overlayOpen = !!(scanForKey || packError || packConfirm);

  // Estado: pedido ya entregado (vive en 'pedidos_entregados') — solo lectura
  if (pedido.estado === "ENTREGADO") {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
          <VolverListaPedidos to="/pedidos" />
          <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Pedido #{pedido.numero}
              </p>
              <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                {pedido.cliente}
              </h1>
            </div>
            <span style={{
              flexShrink: 0,
              background: "#f0fdf4", color: "#15803d",
              fontSize: "0.7rem", fontWeight: 700,
              padding: "3px 10px", borderRadius: "999px",
              letterSpacing: "0.04em", marginTop: "4px",
              border: "1px solid #86efac",
            }}>
              ☑️ ENTREGADO
            </span>
          </div>
          {(pedido.metodoPedido || pedido.deposito) && (
            <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
              {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px",
            padding: "14px 16px", marginBottom: "16px", textAlign: "center",
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: "#15803d", fontSize: "0.95rem" }}>
              Pedido entregado
            </p>
          </div>
        </div>

        <ListaProductosSoloLectura pedido={pedido} catalogoMap={catalogoMap} />
      </div>
    );
  }

  // Estado: pedido completado (R8) — solo si la transición ocurrió recién
  if (pedido.estado === ESTADOS.PREPARADO && initialEstadoRef.current !== ESTADOS.PREPARADO) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "16px"
      }}>
        <div style={{ fontSize: "4rem", lineHeight: 1 }}>✅</div>
        <h2 style={{ fontWeight: 700, fontSize: "1.4rem", margin: 0 }}>¡Pedido preparado!</h2>
        <p style={{ color: "#64748b", margin: 0 }}>Volviendo a tus pedidos…</p>
      </div>
    );
  }

  // Estado: ISABELA acaba de finalizar la preparación (CONTROLADO recién alcanzado)
  if (pedido.estado === ESTADOS.CONTROLADO && esIsabela && initialEstadoRef.current !== ESTADOS.CONTROLADO) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "16px"
      }}>
        <div style={{ fontSize: "4rem", lineHeight: 1 }}>✅</div>
        <h2 style={{ fontWeight: 700, fontSize: "1.4rem", margin: 0 }}>¡Pedido controlado!</h2>
        <p style={{ color: "#64748b", margin: 0 }}>Volviendo a tus pedidos…</p>
      </div>
    );
  }

  // Estado: ISABELA — pedido ya controlado, esperando que ventas lo despache (solo lectura)
  if (pedido.estado === ESTADOS.CONTROLADO && esIsabela) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
          <VolverListaPedidos to="/pedidos" />
          <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Pedido #{pedido.numero}
              </p>
              <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                {pedido.cliente}
              </h1>
            </div>
            <span style={{
              flexShrink: 0,
              background: "#f0fdf4", color: "#15803d",
              fontSize: "0.7rem", fontWeight: 700,
              padding: "3px 10px", borderRadius: "999px",
              letterSpacing: "0.04em", marginTop: "4px",
              border: "1px solid #86efac",
            }}>
              ✅ LISTO
            </span>
          </div>
          {(pedido.metodoPedido || pedido.deposito) && (
            <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
              {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px",
            padding: "14px 16px", marginBottom: "16px", textAlign: "center",
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: "#15803d", fontSize: "0.95rem" }}>
              Esperando despacho
            </p>
            <p style={{ margin: "4px 0 0", color: "#166534", fontSize: "0.8rem" }}>
              Este pedido ya fue controlado. Ventas se encarga del despacho.
            </p>
          </div>
        </div>

        <ListaProductosSoloLectura pedido={pedido} catalogoMap={catalogoMap} />
      </div>
    );
  }

  // Estado: iniciando
  if (pedido.estado === ESTADOS.ASIGNADO || autoStarting) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "12px"
      }}>
        <div style={{ fontSize: "2rem" }}>⏳</div>
        <p style={{ color: "#64748b" }}>Iniciando preparación…</p>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "100px" }}>

      {/* ── Header ─────────────────────────────── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
        {/* ISABELA operario → pipeline; encargado (abre su parte) → tablero; R8 operario → su lista */}
        <VolverListaPedidos to={esIsabela || puedeVincularKits ? "/pedidos" : "/operario/asignados"} />
        <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Pedido #{pedido.numero}
            </p>
            <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
              {pedido.cliente}
            </h1>
          </div>
          <span style={{
            flexShrink: 0,
            background: "#fef9c3", color: "#854d0e",
            fontSize: "0.7rem", fontWeight: 700,
            padding: "3px 10px", borderRadius: "999px",
            letterSpacing: "0.04em", marginTop: "4px"
          }}>
            EN PREPARACIÓN
          </span>
        </div>
        {/* Método de entrega (R8): GIRA / CAMION / RETIRA / AGENCIA — bien visible para el operario */}
        {pedido.metodoEntrega && (() => {
          const c = {
            AGENCIA: { bg: "#eff6ff", color: "#1d4ed8", icon: "🏢" },
            RETIRA:  { bg: "#f0fdf4", color: "#15803d", icon: "🤝" },
            CAMION:  { bg: "#fef3c7", color: "#92400e", icon: "🚚" },
            GIRA:    { bg: "#fdf4ff", color: "#7e22ce", icon: "🚌" },
          }[pedido.metodoEntrega] || { bg: "#f1f5f9", color: "#475569", icon: "📦" };
          return (
            <div style={{ marginTop: "8px" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: c.bg, color: c.color,
                fontSize: "0.82rem", fontWeight: 800,
                padding: "5px 12px", borderRadius: "999px",
                letterSpacing: "0.03em",
              }}>
                {c.icon} Entrega: {pedido.metodoEntrega}
              </span>
            </div>
          );
        })()}
        {(pedido.metodoPedido || pedido.deposito) && (
          <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
            {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>

      {/* ── Aviso de modificaciones del pedido ──── */}
      {modInfo && (modInfo.removedItems || []).length > 0 && (
        <div style={{ margin: "12px 16px 0", background: "#fff7ed", border: "1.5px solid #f97316", borderRadius: "12px", padding: "10px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9a3412", fontWeight: 600 }}>
            Este pedido tuvo modificaciones — algunos productos fueron quitados o cambiaron.
          </p>
        </div>
      )}

      {/* ── Progreso ────────────────────────────── */}
      <div style={{ padding: "16px 16px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "0.82rem", color: "#475569", fontWeight: 600 }}>
            {checkedCount === totalProductos && totalProductos > 0
              ? "Todos los productos cargados"
              : `${checkedCount} de ${totalProductos} productos cargados`}
          </span>
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: checkedCount === totalProductos ? "#16a34a" : "#475569" }}>
            {progreso}%
          </span>
        </div>
        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: "999px",
            background: checkedCount === totalProductos ? "#16a34a" : "#3b82f6",
            width: `${progreso}%`,
            transition: "width 0.3s ease, background 0.3s ease"
          }} />
        </div>
      </div>

      {/* ── Lista de productos ───────────────────── */}
      {lite ? (
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {totalProductos === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 16px", color: "#94a3b8" }}>
              <p style={{ margin: 0 }}>No hay productos en este pedido.</p>
            </div>
          ) : (
            itemsCargables.map((it, i) => {
              const k = it._key;
              const cargado = !!checks[k];
              const qty = reqQty(it);
              const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
              const uru = toURUCode(raw);
              const cat = catalogoMap[uru] || {};
              const nombre = getNombreProducto(it, cat);
              const color = cat?.finish || cat?.color || "";
              const modBadge = getModBadge(uru);
              const esCompDeKit = !!it._kitUru;
              const prev = itemsCargables[i - 1];
              const primeroDelKit = esCompDeKit && (!prev || prev._kitUru !== it._kitUru);

              return (
                <div key={k}>
                {primeroDelKit && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "6px 0 4px", padding: "7px 12px", background: "#f3e8ff", borderRadius: "10px" }}>
                    <span style={{ fontSize: "0.66rem", fontWeight: 800, color: "#7c3aed", background: "#fff", padding: "2px 8px", borderRadius: "999px" }}>🧩 KIT</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: "0.82rem", fontWeight: 700, color: "#6b21a8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it._kitNombre}</span>
                    {it._kitNumero && <span style={{ flexShrink: 0, fontSize: "0.66rem", color: "#7c3aed" }}>ped #{it._kitNumero}</span>}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleCheck(k)}
                  style={{
                    display: "flex", alignItems: "center", gap: "14px",
                    width: "100%", textAlign: "left",
                    background: cargado ? "#f0fdf4" : "#fff",
                    border: `1.5px solid ${cargado ? "#86efac" : esCompDeKit ? "#ddd6fe" : modBadge ? "#fde68a" : "#e2e8f0"}`,
                    borderLeft: esCompDeKit ? "4px solid #a78bfa" : undefined,
                    borderRadius: "14px",
                    padding: "14px 16px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: cargado ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Indicador check */}
                  <div style={{
                    flexShrink: 0,
                    width: "28px", height: "28px",
                    borderRadius: "50%",
                    border: `2px solid ${cargado ? "#16a34a" : "#cbd5e1"}`,
                    background: cargado ? "#16a34a" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s ease",
                  }}>
                    {cargado && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2.5 7L5.5 10L11.5 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>

                  {/* Info producto */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <p style={{
                        margin: 0,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: cargado ? "#15803d" : "#1e293b",
                        lineHeight: 1.35,
                        textDecoration: cargado ? "line-through" : "none",
                        wordBreak: "break-word",
                      }}>
                        {nombre}
                      </p>
                      {modBadge && (
                        <span style={{
                          fontSize: "0.62rem", fontWeight: 800,
                          padding: "2px 7px", borderRadius: 999,
                          background: modBadge.bg, color: modBadge.color,
                          letterSpacing: "0.06em", textTransform: "uppercase", flexShrink: 0,
                        }}>
                          {modBadge.label}
                        </span>
                      )}
                    </div>
                    {color && (
                      <span style={{ display: "inline-block", marginTop: "5px", fontSize: "0.74rem", fontWeight: 800, color: "#0f172a", background: "#e2e8f0", padding: "2px 10px", borderRadius: "6px", textTransform: "capitalize", letterSpacing: "0.02em" }}>{color}</span>
                    )}
                  </div>

                  {/* Cantidad */}
                  <div style={{
                    flexShrink: 0,
                    background: cargado ? "#dcfce7" : "#f1f5f9",
                    color: cargado ? "#15803d" : "#475569",
                    fontWeight: 700, fontSize: "0.85rem",
                    padding: "4px 10px", borderRadius: "8px",
                  }}>
                    ×{qty}
                  </div>
                </button>
                </div>
              );
            })
          )}

          {/* Marcar todo (solo si quedan pendientes) */}
          {totalProductos > 0 && !allChecked && (
            <button
              type="button"
              onClick={marcarTodo}
              style={{
                width: "100%", padding: "10px",
                background: "transparent", border: "1.5px dashed #cbd5e1",
                borderRadius: "12px", color: "#64748b",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Marcar todos como cargados
            </button>
          )}

          {/* ── Problema activo ── */}
          {pedido?.problema?.estado === "PENDIENTE" && (
            <div style={{ background: "#fff7ed", border: "1.5px solid #fb923c", borderRadius: "12px", padding: "12px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: "0.84rem", color: "#9a3412", fontWeight: 600 }}>
                Problema reportado — esperando al encargado
              </p>
            </div>
          )}

          {/* ── Reportar problema ── */}
          {!pedido?.problema || pedido?.problema?.estado === "REVISADO" ? (
            <button
              type="button"
              onClick={() => {
                setProblemaForm({ productoIdxs: [], tipo: "FALTA_STOCK", desc: "" });
                setShowProblema(true);
              }}
              style={{
                width: "100%", padding: "10px",
                background: "transparent", border: "1.5px dashed #fca5a5",
                borderRadius: "12px", color: "#ef4444",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              ⚠️ Reportar un problema con el pedido
            </button>
          ) : null}
        </div>
      ) : (
        /* ── Modo escáner (sin cambios) ── */
        <div style={{ padding: "12px 16px" }}>
          <div className="space-y-2">
            {productosOperario.map((it, i) => {
              const k = keyFor(it, i);
              const need = reqQty(it);
              const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
              const uru = toURUCode(raw);
              const cat = catalogoMap[uru] || {};
              const customer = cat?.customerNo || cat?.customer_no || "—";
              const color = cat?.finish || cat?.color || "—";
              const p = prep[k] || { stock: 0, usarSueltas: 0, paquete: null, usarDePaquete: 0 };
              const usados = (p.usarSueltas || 0) + (p.usarDePaquete || 0);
              const ok = usados >= need && need > 0;

              const setP = (changes) =>
                setPrep(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...changes } }));

              return (
                <div key={k} className="card product-card">
                  <div className="product-card__head">
                    <div className="product-card__title">
                      <span className="product-card__sku">{customer}</span>
                      <span className="product-card__color">{color}</span>
                    </div>
                    <div className="product-card__chips">
                      <span className="chip chip--muted">Req: {need}</span>
                      <span className={`chip ${ok ? "chip--ok" : ""}`}>Usado: {usados}</span>
                    </div>
                  </div>
                  <div className="product-card__meta">
                    <span className="meta-pill">Sueltas: <strong>{Number(p.stock || 0)}</strong></span>
                    <span className="meta-pill">Pack: <strong>{p.paquete?.packSize ? `x${p.paquete.packSize}` : "—"}</strong></span>
                  </div>
                  <div className="qty-row">
                    <span className="qty-row__label">Sueltas</span>
                    <div className="qty">
                      <button type="button" className="qty__btn"
                        onClick={() => setPrep(prev => ({ ...prev, [k]: clampPrepForNeed(k, { usarSueltas: Math.max(0, Number((prev[k] || {}).usarSueltas || 0) - 1) }) }))}
                        disabled={Number(p.usarSueltas || 0) <= 0}>−</button>
                      <span className="qty__val">{Number(p.usarSueltas || 0)}</span>
                      <button type="button" className="qty__btn"
                        onClick={() => setPrep(prev => { const cur = prev[k] || {}; const usedNow = Number(cur.usarSueltas || 0) + Number(cur.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const stockLeft = Number(cur.stock || 0) - Number(cur.usarSueltas || 0); const step = Math.min(remaining, stockLeft); return { ...prev, [k]: clampPrepForNeed(k, { usarSueltas: Number(cur.usarSueltas || 0) + (step > 0 ? 1 : 0) }) }; })}
                        disabled={(() => { const usedNow = Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const stockLeft = Number(p.stock || 0) - Number(p.usarSueltas || 0); return remaining <= 0 || stockLeft <= 0; })()}>+</button>
                    </div>
                  </div>
                  {p.paquete ? (
                    <div className="qty-row">
                      <span className="qty-row__label">Paquete</span>
                      <div className="qty">
                        <button type="button" className="qty__btn"
                          onClick={() => setPrep(prev => { const cur = prev[k] || {}; return { ...prev, [k]: { ...cur, usarDePaquete: Math.max(0, Number(cur.usarDePaquete || 0) - 1) } }; })}
                          disabled={Number(p.usarDePaquete || 0) <= 0}>−</button>
                        <span className="qty__val">{Number(p.usarDePaquete || 0)}</span>
                        <button type="button" className="qty__btn"
                          onClick={() => setPrep(prev => { const cur = prev[k] || {}; const usedNow = Number(cur.usarSueltas || 0) + Number(cur.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const packSize = Number(cur.paquete?.packSize || 0); const roomInPack = Math.max(0, packSize - Number(cur.usarDePaquete || 0)); const stepAllowed = Math.min(remaining, roomInPack); return { ...prev, [k]: { ...cur, usarDePaquete: Number(cur.usarDePaquete || 0) + (stepAllowed > 0 ? 1 : 0) } }; })}
                          disabled={(() => { const usedNow = Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const packSize = Number(p.paquete?.packSize || 0); const roomInPack = Math.max(0, packSize - Number(p.usarDePaquete || 0)); return remaining <= 0 || roomInPack <= 0; })()}>+</button>
                      </div>
                    </div>
                  ) : (
                    <div className="qty-row qty-row--hint">
                      <span className="qty-row__label">Paquete</span>
                      <span className="hint">—</span>
                    </div>
                  )}
                  <button type="button" className="btn btn--secondary btn--full mt-2"
                    onClick={() => setScanForKey(String(k))}>
                    Escanear paquete
                  </button>
                </div>
              );
            })}
          </div>
          <button type="button"
            className="btn w-full bg-black text-white disabled:opacity-60"
            style={{ marginTop: "16px" }}
            onClick={finalizarPreparacion}
            disabled={saving || !(pedido?.productos || []).every((it, i) => {
              const k = keyFor(it, i);
              const p = prep[k] || {};
              return (Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0)) >= reqQty(it);
            })}>
            {saving ? "Guardando…" : "Finalizar preparación"}
          </button>
        </div>
      )}

      {/* ── CTA fijo abajo (solo modo lite) ────── */}
      {lite && (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px 20px",
          background: "#fff",
          borderTop: "1px solid #e2e8f0",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
        }}>
          <button
            type="button"
            onClick={finalizarPreparacion}
            disabled={!allChecked || saving}
            style={{
              width: "100%",
              padding: "15px",
              borderRadius: "14px",
              border: "none",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: allChecked ? "pointer" : "not-allowed",
              transition: "all 0.2s ease",
              background: allChecked ? "#16a34a" : "#e2e8f0",
              color: allChecked ? "#fff" : "#94a3b8",
            }}
          >
            {saving
              ? "Guardando…"
              : allChecked
              ? "✓ Marcar pedido como preparado"
              : `Faltan ${totalProductos - checkedCount} producto${totalProductos - checkedCount !== 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      {/* ── Overlay: Reportar problema ── */}
      {showProblema && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "flex-end" }}>
          <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 16px 40px", width: "100%", maxHeight: "92vh", overflow: "auto" }}>
            {/* Título */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.1rem", color: "#0f172a" }}>Reportar problema</h2>
              <button
                type="button"
                onClick={() => setShowProblema(false)}
                style={{ background: "none", border: "none", fontSize: "1.2rem", color: "#94a3b8", cursor: "pointer", padding: "4px 8px" }}
              >
                ✕
              </button>
            </div>

            {/* Seleccionar producto(s) — se pueden marcar varios */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              ¿En qué producto(s)? <span style={{ fontWeight: 500, textTransform: "none" }}>— podés marcar varios</span>
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "20px" }}>
              {(pedido?.productos || []).map((it, i) => {
                const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
                const uru = toURUCode(raw);
                const cat = catalogoMap[uru] || {};
                const nombre = getNombreProducto(it, cat);
                const qty = reqQty(it);
                const selected = problemaForm.productoIdxs.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setProblemaForm(p => ({
                      ...p,
                      productoIdxs: p.productoIdxs.includes(i)
                        ? p.productoIdxs.filter(x => x !== i)
                        : [...p.productoIdxs, i],
                    }))}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "11px 14px", textAlign: "left", width: "100%",
                      background: selected ? "#eff6ff" : "#f8fafc",
                      border: `1.5px solid ${selected ? "#3b82f6" : "#e2e8f0"}`,
                      borderRadius: "12px", cursor: "pointer",
                      fontWeight: selected ? 600 : 400, fontSize: "0.88rem",
                      color: selected ? "#1d4ed8" : "#1e293b",
                      transition: "all 0.1s ease",
                    }}
                  >
                    {/* Checkbox */}
                    <span style={{
                      flexShrink: 0, width: "22px", height: "22px", borderRadius: "6px",
                      border: `2px solid ${selected ? "#3b82f6" : "#cbd5e1"}`,
                      background: selected ? "#3b82f6" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: "0.8rem", fontWeight: 800,
                    }}>{selected ? "✓" : ""}</span>
                    <span style={{ flex: 1, fontSize: "0.85rem", lineHeight: 1.35, wordBreak: "break-word" }}>{nombre}</span>
                    <span style={{ flexShrink: 0, marginLeft: "8px", background: selected ? "#dbeafe" : "#f1f5f9", color: selected ? "#1d4ed8" : "#64748b", fontWeight: 700, fontSize: "0.78rem", padding: "2px 7px", borderRadius: "6px" }}>
                      ×{qty}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Tipo de problema */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              ¿Cuál es el problema?
            </p>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              {[["FALTA_STOCK", "📦 Falta de stock"], ["OTRO", "⚠️ Otro motivo"]].map(([val, label]) => {
                const sel = problemaForm.tipo === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setProblemaForm(p => ({ ...p, tipo: val }))}
                    style={{
                      flex: 1, padding: "12px 8px",
                      background: sel ? "#fef2f2" : "#f8fafc",
                      border: `1.5px solid ${sel ? "#f87171" : "#e2e8f0"}`,
                      borderRadius: "12px", cursor: "pointer",
                      fontWeight: 600, fontSize: "0.85rem",
                      color: sel ? "#dc2626" : "#475569",
                      transition: "all 0.1s ease",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Descripción */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Descripción (opcional)
            </p>
            <textarea
              value={problemaForm.desc}
              onChange={e => setProblemaForm(p => ({ ...p, desc: e.target.value }))}
              placeholder={problemaForm.tipo === "FALTA_STOCK"
                ? "Ej: Solo hay 3 y necesito 5…"
                : "Describí el problema brevemente…"}
              rows={3}
              style={{
                width: "100%", padding: "12px",
                border: "1.5px solid #e2e8f0", borderRadius: "12px",
                fontSize: "0.9rem", resize: "none",
                boxSizing: "border-box", marginBottom: "16px",
              }}
            />

            {/* CTA */}
            <button
              type="button"
              onClick={reportarProblema}
              disabled={reportando}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                background: "#dc2626", color: "#fff",
                fontWeight: 700, fontSize: "1rem", cursor: "pointer",
              }}
            >
              {reportando ? "Reportando…" : "Reportar al encargado"}
            </button>
          </div>
        </div>
      )}

      {/* ── Overlay escáner (solo modo completo) ── */}
      {!lite && overlayOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.62)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
        }}>
          <div className="card" style={{
            width: "100%", maxWidth: 420, maxHeight: "92vh", overflow: "auto",
            background: "#fff", borderRadius: 12,
            boxShadow: "0 20px 50px rgba(0,0,0,.35)",
          }}>
            <div className="subsection-title" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
              <span>{scanForKey ? "Escanear paquete" : packError ? "Paquete incorrecto" : "Confirmar paquete"}</span>
              <button type="button" className="btn btn--ghost"
                onClick={() => { setScanForKey(null); setPackError(null); setPackConfirm(null); }}>
                Cerrar
              </button>
            </div>
            <div className="p-3">
              {scanForKey && !packConfirm && !packError && (
                <>
                  <QrScanner
                    key={scanForKey}
                    onScanSuccess={(payload) => onScanPaquete(payload)}
                    onError={(err) => { console.error(err); toast?.error?.("Error con la cámara"); setScanForKey(null); }}
                  />
                  <p className="muted text-center mt-2" style={{ fontSize: "0.8rem" }}>
                    Apuntá la cámara al código QR del paquete
                  </p>
                </>
              )}
              {packError && (
                <PackErrorModal
                  esperado={packError.esperado}
                  qr={packError.qr}
                  onRetry={() => { setPackError(null); setScanForKey(scanForKey); }}
                  onClose={() => { setPackError(null); setScanForKey(null); }}
                />
              )}
              {packConfirm && (
                <PackConfirmModal
                  {...packConfirm}
                  onConfirm={(qty) => {
                    const k = packConfirm.key;
                    setPrep(prev => {
                      const cur = prev[k] || {};
                      return { ...prev, [k]: clampPrepForNeed(k, { paquete: { packSize: packConfirm.packSize }, usarDePaquete: qty }) };
                    });
                    setPackConfirm(null);
                  }}
                  onClose={() => setPackConfirm(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
